var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * @module cercaliagl/control/Address
 */
import LonLat from '../LonLat';
import Marker from '../Marker';
import Feature from '../Feature';
import SimpleLabel from '../SimpleLabel';
import Control from './Control';
import MapControls from './MapControls';
import Notification, { Severity } from '../Notification';
import ReverseGeocoding from '../service/ReverseGeocoding';
import Geocoding from '../service/Geocoding';
import GeometryEntity from '../service/GeometryEntity';
import { downloadKmlFromWkt, formatAddress } from '../util';
import { i18n } from '../dictionary';
import VAddress, { EventType } from '../view/VAddress';
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const control = new cercaliagl.control.Address();
 * map.addControl(control);
 * ```
 * <br/>
 * Class <b/>cercaliagl.control.Address<b> is used for internal purposes, but can be accessed throught methods.
 * This methods apply actions to Adress UI component.
 * By default it appears in bottom-left corner. It shows address summary address and detailed address with all geoentity components
 *
 * Usage getting address control from map:
 *
 * ```js
 * const map = new cercaliagl.Map({ target: 'map' });
 * const address = map.getControlByName(cercaliagl.control.MapControls.ADDRESS);
 * address.getDirection(new cercaliagl.LonLat(-3.682, 40.43059), (data) => console.log(data));
 * ```
 *
 * @extends {Control}
 * @api
 */
var Address = /** @class */ (function (_super) {
    __extends(Address, _super);
    /**
     * @constructor
     */
    function Address() {
        var _this = this;
        var addressView = new VAddress();
        var element = addressView.getElement();
        _this = _super.call(this, {
            element: element,
            name: MapControls.ADDRESS,
            className: 'cercaliagl.control.Adddress',
        }) || this;
        /**
         * @private
         * @type {VAddress}
         */
        _this.addressView_ = addressView;
        /**
         * @private
         * @type {HTMLElement}
         */
        _this.element_ = element;
        /**
         * @private
         * @type {Marker|null}
         */
        _this.markerAddress_ = null;
        /**
         * @private
         * @type {Feature|null}
         */
        _this.featureGeometryEntity_ = null;
        /**
         * @private
         * @type {ReverseGeocoding}
         */
        _this.reverseGeocodingService_ = new ReverseGeocoding();
        /**
         * @private
         * @type {Geocoding}
         */
        _this.geocodingService_ = new Geocoding();
        /**
         * @private
         * @type {GeometryEntity}
         */
        _this.geomEntService_ = new GeometryEntity();
        /**
         * @private
         * @type {Function|null}
         */
        _this.searchAddressCallbackFn_ = null;
        /**
         * @private
         * @type {Function|null}
        */
        _this.closeDialogCallbackHandler_ = null;
        _this.initialize_();
        return _this;
    }
    /**
     * @override
     * @param {import("maplibre-gl").Map} map Mapbox map
     */
    Address.prototype.onAdd = function (map) {
        this.map_ = map;
        return this.element_;
    };
    /**
     * @private
     */
    Address.prototype.initialize_ = function () {
        this.addressView_.on(EventType.DIRECTION_TO, this.handleDirectionTo_.bind(this));
        this.addressView_.on(EventType.SAVE_GEOMETRY, this.handleSaveGeometry_.bind(this));
    };
    Address.prototype.setCloseAddressDialogCallback = function (callback) {
        if (this.closeDialogCallbackHandler_) {
            this.addressView_.removeEventListener(EventType.CLOSED, this.closeDialogCallbackHandler_);
        }
        this.closeDialogCallbackHandler_ = function () { return callback(); };
        this.addressView_.on(EventType.CLOSED, this.closeDialogCallbackHandler_);
    };
    /**
     * Set current address to routing destination. If there is navbar enabled destination route is
     * assigned with current opened address
     * @api
     */
    Address.prototype.directionTo = function () {
        if (this.addressView_.getGeoEntity()) {
            this.removeGeometryEntity_();
            this.removeAddressMarker_();
            this.addressView_.clickDirectionTo();
        }
    };
    /**
     * @private
     * @param {Event} event Event
     */
    Address.prototype.handleDirectionTo_ = function (event) {
        var map = this.getCercaliaMap();
        var mapRouting = map.getMapRouting();
        this.removeAddressMarker_();
        mapRouting.routeFromCurrentPositionToDestination(event);
    };
    /**
     * @private
     */
    Address.prototype.handleSaveGeometry_ = function () {
        var wkt = this.geomEntService_.getWKT();
        if (wkt) {
            downloadKmlFromWkt(wkt, 'cercalia_geoment.kml');
        }
    };
    /**
     * Open a direction using GeEntity from server response.
     * Then it shows addres UI with filled data about address components
     * @param {import('../servers').GeEntity} ge GeEntity.
     * @param {boolean=} [panTo] Pan Zoom to position.
     * @api
     */
    Address.prototype.goToGeEntity = function (ge, panTo) {
        if (panTo === void 0) { panTo = true; }
        var lonLat = new LonLat(ge.coord.x, ge.coord.y);
        this.removeAddressMarker_();
        this.removeGeometryEntity_();
        this.showGeoEntity_(ge, ge.type);
        this.addressView_.toggleBtnSaveKml(false);
        if (ge.municipality && (ge.name.value === ge.municipality.value)) {
            // Municipality level (locality and municipality with the same names.)
            var municipalityId = ge.municipality.id;
            if (municipalityId) {
                this.geomEntService_.getMunicipalityGeometry(municipalityId, this.handleDrawGeometryEntity_.bind(this, panTo, lonLat));
            }
        }
        else if (ge.type === 'pc') {
            var countryId = ge.country.id;
            var pcode = ge.name.value;
            if (countryId && pcode) {
                this.geomEntService_.getPostalCodeGeometry(countryId, pcode, this.handleDrawGeometryEntity_.bind(this, panTo, lonLat));
            }
        }
        else if (ge.type === 'ctry') {
            this.geomEntService_.getPostalCodeShortGeometry(ge.pc2, this.handleDrawGeometryEntity_.bind(this, panTo, lonLat));
        }
        else {
            var map = this.getCercaliaMap();
            var zoom = (ge.type === 'st' || ge.type === 'adr' || ge.type === 'p') ? 17 : 12;
            map.setCenter(lonLat, zoom);
        }
        var text = formatAddress(ge, ge.type);
        this.createAddressMarker_(lonLat, text);
    };
    /**
     * Get direction giving position and shows response in UI address component.
     * Optionally, you can provide addition callback function.
     * @param {LonLat} lonLat Position
     * @param {(function(LonLat, import('../servers').GeEntity) : void)=} [callbackFn] Callback function
     * @param {boolean} hideMarker Disables the default option to create a marker on the position
     * @param {boolean} hidePopUp Disables the default option to show a popup with the position information
     * @param {boolean} preserveOriginalPosition If true, the original position is preserved, false by default
     * @api
     */
    Address.prototype.getDirection = function (lonLat, callbackFn, hideMarker, hidePopUp) {
        var _this = this;
        this.removeGeometryEntity_();
        this.removeAddressMarker_();
        this.reverseGeocodingService_.getDirection(lonLat, function (data) {
            var gelist = data.cercalia.proximity.gelist;
            var type = gelist.type;
            var ge = gelist.ge;
            _this.showGeoEntity_(ge, type, hidePopUp);
            var text = formatAddress(ge, type);
            if (!hideMarker) {
                _this.createAddressMarker_(lonLat, text);
            }
            if (callbackFn) {
                callbackFn(lonLat, ge);
            }
            else if (_this.searchAddressCallbackFn_) {
                _this.searchAddressCallbackFn_(lonLat, data);
            }
        });
    };
    /**
     * Add a custom callback function called when a address is searched via context menu map.
     * This function will receive {@link cercalia.LonLat LonLat} and {@link cercaliax.ProximityResult ProximityResult}
     * @param {Function|null} callbackFn Callback function
     * @api
     */
    Address.prototype.setSearchAddressCallbackFunction = function (callbackFn) {
        this.searchAddressCallbackFn_ = callbackFn;
    };
    /**
     * @private
     * @param {import('../servers').GeEntity} ge GeEntity.
     * @param {string} type Ge Type. `adr`, `st` or `adr`.
     * @param {boolean} hidePopUp Disables the default option to show a popup with the position information
     */
    Address.prototype.showGeoEntity_ = function (ge, type, hidePopUp) {
        this.addressView_.toggleBtnSaveKml(false);
        this.viewInterface_.closePopups();
        this.addressView_.reset();
        if (type !== 'p') {
            this.addressView_.setGeoEntity(ge, type);
        }
        else {
            this.addressView_.setPoiEntity(ge);
        }
        if (!hidePopUp) {
            this.addressView_.show();
        }
    };
    /**
     * @private
     * @param {LonLat} lonLat Position
     * @param {string} text Text.
     */
    Address.prototype.createAddressMarker_ = function (lonLat, text) {
        var map = this.getCercaliaMap();
        this.markerAddress_ = new Marker(/** @type {import('../Marker').MarkerOptions} */ ({
            position: lonLat,
            simpleLabel: new SimpleLabel(/** @type {import('../SimpleLabel').SimpleLabelOptions} */ ({ text: text })),
        }));
        map.addMarker(this.markerAddress_);
    };
    /**
     * Center map to coordinates and draw a marker with a label with position coordinates.
     * It doesn't obtain reverse geocoding data.
     * @param {LonLat} lonLat Position.
     * @param {boolean} [panTo] Pan Zoom to position.
     * @api
     */
    Address.prototype.goToCoordinates = function (lonLat, panTo) {
        var map = this.getCercaliaMap();
        var text = "".concat(lonLat.getLon(), ",").concat(lonLat.getLat());
        this.removeAddressMarker_();
        this.createAddressMarker_(lonLat, text);
        if (panTo) {
            map.setCenter(lonLat, 15, true);
        }
    };
    /**
     * Make a geocoding request to obtain postal code by country. It shows a marker and draw a polygon with its extension.
     * Once postal code data is returned this function pan to postal code centroid. Optionally is possible to disable this pan.
     * @param {string} countryId Country Id. For example: `ESP` or `FRA`.
     * @param {string} pcode Postal code.
     * @param {boolean=} [panTo] Optional pan to location.
     * @api
     */
    Address.prototype.goToPostalcode = function (countryId, pcode, panTo) {
        if (panTo === void 0) { panTo = true; }
        this.geocodingService_.clear();
        // Añadimos parámetros de la nueva busqueda
        this.geocodingService_.setSearch(/** @type {import('../service/Geocoding').GeocodingOptions} */ ({
            countryId: countryId,
            postalCode: pcode,
        }));
        // Buscamos i tratamos el resultado
        this.removeGeometryEntity_();
        this.geocodingService_.geocode(this.handlePostalCodeGeocoding_.bind(this, pcode));
        this.geomEntService_.getPostalCodeGeometry(countryId, pcode, this.handleDrawGeometryEntity_.bind(this, panTo, null));
    };
    /**
     * @private
     * @param {string} pcode Postal code.
     * @param {import('../servers').GeocodingResult} data Geocoding result.
     */
    Address.prototype.handlePostalCodeGeocoding_ = function (pcode, data) {
        var notification;
        var cercaliaMap = this.getCercaliaMap();
        if (data.cercalia.error !== undefined) {
            notification = new Notification(i18n('notificationErrorGetPostalCode') + data.cercalia.error, Severity.ERROR);
            notification.display(cercaliaMap);
        }
        else if (data.cercalia.candidates.candidate) {
            var ge = data.cercalia.candidates.candidate[0].ge;
            var countryName = ge.country.value;
            var lonLat = new LonLat(ge.coord.x, ge.coord.y);
            cercaliaMap.setCenter(lonLat, 14, false);
            this.showGeoEntity_(ge, ge.type);
            this.removeAddressMarker_();
            var text = "".concat(pcode, ", ").concat(countryName);
            this.createAddressMarker_(lonLat, text);
        }
        else {
            notification = new Notification(i18n('notificationNoCandidates'), Severity.ERROR);
            notification.display(cercaliaMap);
        }
    };
    /**
     * Clear all painted overlays on the map related to this control
     * @api
     */
    Address.prototype.clean = function () {
        this.addressView_.reset();
        this.removeAddressMarker_();
        this.removeGeometryEntity_();
        this.closePopup();
    };
    /**
     * Closes address UI address element
     * @api
     */
    Address.prototype.closePopup = function () {
        this.addressView_.hide();
    };
    /**
     * @private
     */
    Address.prototype.removeAddressMarker_ = function () {
        if (this.markerAddress_) {
            var map = this.getCercaliaMap();
            map.removeMarker(this.markerAddress_);
            this.markerAddress_ = null;
        }
    };
    /**
     * @private
     */
    Address.prototype.removeGeometryEntity_ = function () {
        if (this.featureGeometryEntity_ !== null) {
            var map = this.getCercaliaMap();
            map.removeFeature(this.featureGeometryEntity_);
            this.featureGeometryEntity_ = null;
        }
    };
    /**
     * @private
     * @param {boolean} panTo Pan to location.
     * @param {LonLat} lonLat Positional
     * @param {import('../servers').GeometryEntityResult} data Geocoding result.
     */
    Address.prototype.handleDrawGeometryEntity_ = function (panTo, lonLat, data) {
        var map = this.getCercaliaMap();
        if (!data.cercalia.error) {
            var geometry = data.cercalia.ge.geometry;
            var wkt = geometry.wkt.value;
            if (wkt) {
                this.featureGeometryEntity_ = new Feature({
                    strokeColor: '#ff4d4d',
                    strokeWidth: 1,
                    strokeOpacity: 1,
                    fillColor: '#ff4d4d',
                    fillOpacity: 0.1,
                    outline: false,
                    wkt: wkt,
                });
                map.addFeature(this.featureGeometryEntity_);
                if (panTo) {
                    map.fitBounds(this.featureGeometryEntity_.getBounds());
                }
                this.addressView_.toggleBtnSaveKml(true);
            }
            else {
                map.setCenter(lonLat, 14, false);
            }
        }
    };
    /**
     * Return geoentity data {@link module:cercaliagl/servers~GeEntity} from current address.
     * Returns `null` if this control isn't showing any
     * @return {import('../servers').GeEntity} ge GeEntity.
     * @api
     */
    Address.prototype.getCurrentAddress = function () {
        return this.addressView_.getGeoEntity();
    };
    return Address;
}(Control));
export default Address;
//# sourceMappingURL=Address.js.map